import { useQuery, useMutation } from "@apollo/client/react";
import { useQuery as useReactQuery, useQueryClient } from "@tanstack/react-query";
import { getBrowserSupabaseClient } from "@wtc/supabase";
import { logger } from "@/lib/logger";
import { filterNulls } from "@/lib/utils/crm/graphql-types";
import { prepareEntityGraphQlMutationInput } from "@/lib/utils/crm/prepare-graphql-entity-payload";
import { useMemo } from "react";
import {
  GetTaskssDocument,
  GetTasksDocument,
  CreateTasksDocument,
  UpdateTasksDocument,
  DeleteTasksDocument,
  GetTaskssQuery,
  GetTasksQuery,
  CreateTasksMutation,
  UpdateTasksMutation,
  DeleteTasksMutation,
  Contact_Tasks,
  Contact_TasksInsertInput,
} from "@/src/gql/graphql";
import { TASK_CONFIG } from "@/features/tasks/components/TaskConfig";
import type { EntityHooks } from "@/components/shared/UniversalCRMTable/types/shared";
// Writes go through pg_graphql, not an API route, so the server-side
// mapToUserFacingError never sees them. See mutation-error.ts.
import { crmMutationError } from "@/lib/utils/crm/mutation-error";

// ============================================================================
// HELPER: Fetch User Data for assigned_users
// ============================================================================

async function fetchUserDataForTasks(tasks: Contact_Tasks[]): Promise<Contact_Tasks[]> {
  if (!tasks || tasks.length === 0) return tasks;

  const allUserIds = new Set<string>();
  tasks.forEach((task) => {
    if (task.assigned_users && Array.isArray(task.assigned_users)) {
      filterNulls(task.assigned_users).forEach((item: string | { id: string }) => {
        let userId: string | null = null;

        if (typeof item === "string" && item.trim() !== "") {
          userId = item;
        } else if (
          typeof item === "object" &&
          item !== null &&
          item.id &&
          typeof item.id === "string"
        ) {
          userId = item.id;
        }

        if (userId) {
          allUserIds.add(userId);
        }
      });
    }
  });

  if (allUserIds.size === 0) return tasks;

  try {
    const supabase = getBrowserSupabaseClient();
    const { data: userData, error } = await supabase
      .from("app_users")
      .select("id, first_name, last_name, email")
      .in("id", Array.from(allUserIds));

    if (error) {
      logger.error("Error fetching user data", error);
      return tasks;
    }

    if (userData && userData.length > 0) {
      const userMap = new Map(userData.map((user) => [user.id, user]));

      return tasks.map((task) => ({
        ...task,
        assigned_users:
          task.assigned_users && Array.isArray(task.assigned_users)
            ? filterNulls(task.assigned_users).map((item: string | { id: string }) => {
                if (typeof item === "object" && item !== null && item.id) {
                  return userMap.get(item.id) || item;
                }
                if (typeof item === "string") {
                  return userMap.get(item) || item;
                }
                return item;
              })
            : task.assigned_users,
      })) as Contact_Tasks[];
    }
  } catch (error) {
    logger.error("Error processing assigned_users", error);
  }

  return tasks;
}

type AssignedUserValue = string | { id?: string | null } | null | undefined;

function normalizeAssignedUserIds(
  assignedUsers: AssignedUserValue[] | null | undefined
): string[] | undefined {
  if (!assignedUsers || !Array.isArray(assignedUsers)) {
    return undefined;
  }

  const normalized = assignedUsers
    .map((item) => {
      if (typeof item === "string") return item;
      if (typeof item === "object" && item !== null && typeof item.id === "string") {
        return item.id;
      }
      return null;
    })
    .filter((uid): uid is string => uid !== null);

  return normalized.length > 0 ? normalized : undefined;
}

// Task hooks using generated GraphQL operations
export const useTaskHooks = (): EntityHooks<Contact_Tasks> => ({
  queryDocument: GetTaskssDocument,
  usePaginated: (params?: Record<string, unknown>) => {
    const result = useQuery<GetTaskssQuery>(GetTaskssDocument, {
      errorPolicy: "all",
      notifyOnNetworkStatusChange: true,
      fetchPolicy: "cache-and-network",
      variables: params,
    });

    const nodes = (result.data?.contact_tasksCollection?.edges
      ?.map((edge) => edge?.node)
      .filter((node): node is NonNullable<typeof node> => node != null) || []) as Contact_Tasks[];

    const { data: enrichedNodes, isLoading: isEnrichingData } = useReactQuery({
      queryKey: ["tasks-with-users", nodes.map((n) => n.id).join(",")],
      queryFn: () => fetchUserDataForTasks(nodes),
      enabled: nodes.length > 0 && !result.loading,
      staleTime: 5 * 60 * 1000,
    });

    return {
      data: result.data
        ? {
            data: enrichedNodes || nodes,
            totalCount: result.data.contact_tasksCollection?.totalCount || 0,
            pageInfo: result.data.contact_tasksCollection?.pageInfo,
          }
        : undefined,
      isLoading: result.loading || isEnrichingData,
      error: result.error || null,
      refetch: result.refetch,
      networkStatus: result.networkStatus,
    };
  },
  useInfinite: (_params?: Record<string, unknown>) => {
    return {
      data: undefined,
      isLoading: false,
      error: null,
      refetch: () => {},
      hasNextPage: false,
      isFetchingNextPage: false,
      fetchNextPage: () => {},
    };
  },
  useSingle: (id: string, options?: { enabled?: boolean }) => {
    const result = useQuery<GetTasksQuery>(GetTasksDocument, {
      variables: { id },
      errorPolicy: "all",
      skip: !id || options?.enabled === false,
    });

    const task = result.data?.contact_tasksCollection?.edges?.[0]?.node || null;
    const taskForEnrichment = useMemo(() => task, [task?.id]);

    const { data: enrichedTask, isLoading: isEnrichingData } = useReactQuery({
      queryKey: ["task-with-users", id],
      queryFn: () =>
        taskForEnrichment
          ? fetchUserDataForTasks([taskForEnrichment as Contact_Tasks]).then((tasks) => tasks[0])
          : null,
      enabled: !!taskForEnrichment && !result.loading,
      staleTime: 5 * 60 * 1000,
    });

    return {
      data: enrichedTask || task ? ((enrichedTask || task) as Contact_Tasks) : undefined,
      isLoading: result.loading || isEnrichingData,
      error: result.error || null,
      refetch: result.refetch,
    };
  },

  useUpdate: () => {
    const [mutate, result] = useMutation<UpdateTasksMutation>(UpdateTasksDocument);
    const queryClient = useQueryClient();

    return {
      mutateAsync: async ({ id, ...input }: { id: string } & Partial<Contact_Tasks>) => {
        const cleanInput = { ...input } as Partial<Contact_Tasks> & Record<string, unknown>;

        const normalizedAssignedUsers = normalizeAssignedUserIds(
          cleanInput.assigned_users
        );
        if (normalizedAssignedUsers) {
          cleanInput.assigned_users = normalizedAssignedUsers;
        }

        const prepared = prepareEntityGraphQlMutationInput(
          { ...cleanInput },
          TASK_CONFIG.fields,
          {
            mode: "update",
          }
        );

        const { data, error } = await mutate({
          variables: { id, input: prepared },
          errorPolicy: "all",
        });

        if (error) throw crmMutationError(error, "Couldn't update the task.");

        const updated = data?.updatecontact_tasksCollection?.records?.[0];
        if (!updated) throw new Error("Couldn't update the task.");

        // Patch the user-enriched React Query caches in place. These are keyed
        // by the task id-list only (["tasks-with-users", "<id>,<id>,..."]), so a
        // field change (e.g. status from a board drag) does NOT change the key
        // and the stale enriched array — preferred over the Apollo `nodes` via
        // `enrichedNodes || nodes` — keeps winning until a manual refresh.
        // Merge the mutation result so views (board grouping, table, cards)
        // reflect the change immediately. Preserve the already-enriched
        // `assigned_users` (resolved user objects) unless this mutation actually
        // changed the assignment — `updated` carries raw id strings.
        const updatedTask = updated as Contact_Tasks;
        const assignmentChanged = "assigned_users" in cleanInput;
        const mergeInto = (task: Contact_Tasks): Contact_Tasks => {
          const merged = { ...task, ...updatedTask } as Contact_Tasks;
          if (!assignmentChanged) {
            merged.assigned_users = task.assigned_users;
          }
          return merged;
        };

        queryClient.setQueriesData<Contact_Tasks[]>(
          { queryKey: ["tasks-with-users"] },
          (oldData) => {
            if (!Array.isArray(oldData)) return oldData;
            return oldData.map((task) => (task.id === updatedTask.id ? mergeInto(task) : task));
          }
        );

        // The DETAIL cache is a different key — `["task-with-users", id]`
        // (singular), which the plural prefix above does NOT match. It is keyed
        // by id alone, so a field change never invalidates it, and `useSingle`
        // PREFERS the enriched value (`enrichedTask || task`) — so without this
        // the task detail panel kept rendering the pre-save values after a
        // successful update.
        queryClient.setQueriesData<Contact_Tasks | null>(
          { queryKey: ["task-with-users", updatedTask.id] },
          (oldData) => (oldData ? mergeInto(oldData) : oldData)
        );

        return updatedTask;
      },
      isPending: result.loading,
    };
  },

  useDelete: () => {
    const [mutate, result] = useMutation<DeleteTasksMutation>(DeleteTasksDocument);
    return {
      mutateAsync: async (id: string) => {
        const { data, error } = await mutate({
          variables: { id },
          errorPolicy: "all",
        });
        if (error) throw crmMutationError(error, "Couldn't delete the task.");
        const deletedId = data?.deleteFromcontact_tasksCollection?.records?.[0]?.id;
        if (!deletedId) throw new Error("Couldn't delete the task.");
        return deletedId;
      },
      isPending: result.loading,
    };
  },
  useCreate: () => {
    const [mutate, result] = useMutation<CreateTasksMutation>(CreateTasksDocument);
    return {
      mutateAsync: async (input: Contact_TasksInsertInput) => {
        const cleanInput = prepareEntityGraphQlMutationInput(
          { ...input },
          TASK_CONFIG.fields,
          { mode: "insert" }
        );
        const { data, error } = await mutate({
          variables: { input: cleanInput },
          errorPolicy: "all",
        });
        if (error) throw crmMutationError(error, "Couldn't create the task.");
        const created = data?.insertIntocontact_tasksCollection?.records?.[0];
        if (!created) throw new Error("Couldn't create the task.");
        return created as Contact_Tasks;
      },
      isPending: result.loading,
    };
  },

  getFieldConfig: (fieldName: string) => {
    return TASK_CONFIG.fields[fieldName] || null;
  },

  getTableConfig: () => {
    return TASK_CONFIG;
  },
});
